import React, { useMemo, useCallback } from 'react';
import './SlotTooltip.css';
import StatsList from '../StatsList';
import { WeaponDamageList, WeaponMetaList } from '../weapon/WeaponSummary';
import { hasWeaponDamages, buildWeaponMetaRows } from '../../core/weapon';
import { SLOT_IDX } from '../../core/Build';
import { applyWeaponMagingPreview } from '../../core/weaponMaging';
import { exoCandidates, magedStatRows } from '../../core/itemMaging';
import DropdownMenu from '../utils/DropdownMenu';
import { Add, RestartAlt } from '@mui/icons-material';
import Tooltip from '../utils/Tooltip';
import { buildElementIconFilter } from '../utils/CardGradient';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { localizedText } from '../../utils/localizedText';
import MediaImg from '../utils/MediaImg';
import { useOverlay } from '../../contexts/OverlayContext';
import SearchService from '../../services/SearchService';
import SetHeader from '../search/SetHeader';
import SetView from '../search/SetView';
import OverlayEquipFooter from '../OverlayEquipFooter';
import { useEquipItem } from '../../hooks/useEquipItem';
import {
    hasItemEffect,
    getItemEffectName,
    getItemEffectDescription,
    ITEM_EFFECT_ICON
} from '../../core/itemEffect';

const TXT_CONDITIONS = { fr: 'Conditions', en: 'Conditions', de: 'Bedingungen', es: 'Condiciones', pt: 'Condições' };
const TXT_ADD_EXO = { fr: 'Exo', en: 'Exo', de: 'Exo', es: 'Exo', pt: 'Exo' };
const TXT_ADD_EXO_HINT = {
    fr: 'Ajouter une ligne exotique',
    en: 'Add an exotic line',
    de: 'Exotische Zeile hinzufügen',
    es: 'Añadir una línea exótica',
    pt: 'Adicionar uma linha exótica',
};
const TXT_RESET_MAGING = { fr: 'Réinitialiser', en: 'Reset', de: 'Zurücksetzen', es: 'Restablecer', pt: 'Redefinir' };

const SlotTooltip = React.forwardRef(({
    item,
    build,
    lang = 'fr',
    embedded = false,
    interactive = false,
    onOpenItem = null,
    onOpenSet = null,
    onDismiss = null,
    onMouseLeave = null,
    weaponMaging = null,
    weaponStats = null,
    // Forgemagie: the slot's maged lines, and — in the slot overlay only —
    // what setting one does. See core/itemMaging.js.
    itemMaging = null,
    onMageLine = null,
    onClearMaging = null,
}, ref) => {
    const { showOverlay } = useOverlay();
    const { equipItem, equipSet } = useEquipItem(lang);
    const iconFilterStyle = useMemo(() => ({
        '--item-element-filter-icon': buildElementIconFilter(item?.effects_map || {}),
    }), [item?.effects_map]);

    // The hoverable variant is one big button: a click anywhere but the set
    // name opens the slot's item overlay — the same one a tap opens on touch.
    const handleTooltipClick = useCallback(() => {
        if (!interactive) return;
        onDismiss?.();
        onOpenItem?.();
    }, [interactive, onDismiss, onOpenItem]);

    const handleSetClick = useCallback(async (e) => {
        e.stopPropagation();
        if (!item?.item_set_id) return;
        onDismiss?.();
        // The search grid opens sets its own way — into the slot the search was
        // opened for, or into the blacklist picker's action instead of
        // equipping. Neither is in scope here, so that caller hands its opener
        // in and the default below is for the build slots.
        if (onOpenSet) {
            onOpenSet();
            return;
        }
        try {
            const set = await SearchService.getSet(item.item_set_id, lang);
            showOverlay(
                [
                    <SetHeader key={`set-hdr-${item.item_set_id}`} set={set} lang={lang} />,
                    <SetView
                        key={`set-view-${item.item_set_id}`}
                        set={set}
                        lang={lang}
                        onItemClick={(it) => equipItem(it, null)}
                    />,
                ],
                {
                    footer: (
                        <OverlayEquipFooter
                            lang={lang}
                            onEquip={() => equipSet(set, null)}
                        />
                    ),
                }
            );
        } catch (error) {
            console.error('Failed to fetch set details:', error);
        }
    }, [item?.item_set_id, lang, showOverlay, equipItem, equipSet, onDismiss, onOpenSet]);

    if (!item || !build) return null;
    const info = build.getItemInfo(item, lang);
    if (!info) return null;

    const canMage = embedded && typeof onMageLine === 'function';
    const statRows = magedStatRows(item, itemMaging, canMage);
    const exoChoices = canMage ? exoCandidates(item, itemMaging) : [];
    const rawWeapon = item.weapon || null;
    const weapon = rawWeapon && weaponMaging
        ? applyWeaponMagingPreview(rawWeapon, weaponMaging)
        : rawWeapon;
    const hasWeaponDamage = weapon && hasWeaponDamages(weapon);
    const hasWeaponMeta = weapon && buildWeaponMetaRows(weapon, lang).length > 0;
    // What the weapon actually hits for on this build, the way the search card
    // shows it. The tooltip can only price the build's *own* weapon by itself:
    // the sheet it is read against already has that one on, and any other weapon
    // belongs to no build this tooltip knows. A caller that does know — the
    // weapon slot's search, whose compact tiles have no card to carry the
    // numbers — hands the sheet in, and that wins. Otherwise the dice printed on
    // the item stand, which is what a set thumbnail's tooltip gets.
    const isEquippedWeapon = hasWeaponDamage
        && build.items?.[SLOT_IDX.WEAPON]?.id === item.id;
    const resolvedWeaponStats = weaponStats
        || (isEquippedWeapon ? (build.getStats?.() || null) : null);
    const hasStats = Array.isArray(statRows) && statRows.length > 0;
    const itemTypeName = localizedText(item.type?.name, lang) || null;
    const itemTypeIcon = item.type?.img || null;

    const hasEffect = hasItemEffect(item);
    const effectName = hasEffect ? getItemEffectName(item, lang) : '';
    const effectDescription = hasEffect ? getItemEffectDescription(item, lang) : '';
    const showEffectRow = hasEffect && !!effectName;

    const iconSrc = item.icon ? resolveMediaUrl(item.icon) : '/img/stat_re_armes.png';

    return (
        <div
            ref={ref}
            className={`slot-tooltip column${embedded ? ' slot-tooltip--embedded' : ''}${interactive ? ' slot-tooltip--interactive' : ''}`}
            style={embedded ? iconFilterStyle : undefined}
            onMouseLeave={onMouseLeave || undefined}
            onClick={handleTooltipClick}
        >
            <div className="slot-tooltip-header column ">
                {embedded && (
                    <MediaImg
                        className="slot-tooltip-icon"
                        src={iconSrc}
                        alt={info.name || ''}
                        resolve={false}
                        retry={Boolean(item.icon)}
                    />
                )}
                <div className="slot-tooltip-info row">
                    {itemTypeName && (
                        <h6 className="slot-tooltip-type">
                            {itemTypeIcon && (
                                <img src={itemTypeIcon} className="slot-tooltip-type-icon" alt="" />
                            )}
                            <span className="slot-tooltip-type-text">{itemTypeName}</span>
                        </h6>
                    )}
                    {itemTypeName && info.lvl > 0 && (
                        <span className="slot-tooltip-info-dot" aria-hidden="true">•</span>
                    )}
                    {info.lvl > 0 && (
                        <h6 className="slot-tooltip-level">
                            {/* Wrapped like the card's own level (`.item-card-level-value`):
                                the global `*` rule puts inner text in Urbanist, so a bare
                                number would render in the h6's Poppins and read heavier
                                than the type label beside it, at the same weight. */}
                            <span className="slot-tooltip-level-value">{info.lvl}</span>
                        </h6>
                    )}
                </div>
                <h4 className="slot-tooltip-name">{info.name}</h4>
                {info.set && (
                    embedded || interactive ? (
                        <button
                            type="button"
                            className="slot-tooltip-set-button"
                            onClick={handleSetClick}
                        >
                            {info.set.name}
                        </button>
                    ) : (
                        <span className="slot-tooltip-set">{info.set.name}</span>
                    )
                )}
            </div>
            {hasWeaponDamage && (
                <WeaponDamageList
                    weapon={weapon}
                    lang={lang}
                    className="slot-tooltip-weapon-damages"
                    stats={resolvedWeaponStats}
                    lvl={build.lvl ?? null}
                    // Only the overlay is wide enough to name each line: the
                    // hover tooltip is capped at 15.625rem, where a label
                    // column would come out of the numbers beside it.
                    showLabels={embedded}
                />
            )}
            {hasWeaponDamage && (hasStats || showEffectRow) && (
                <div className="slot-tooltip-divider" />
            )}
            <div className="slot-tooltip-stats-wrapper">
                {canMage && (exoChoices.length > 0 || itemMaging) && (
                    <div className="slot-tooltip-maging-actions row">
                        {exoChoices.length > 0 && (
                            <DropdownMenu
                                dropdownClassName="slot-tooltip-exo-add"
                                menuClassName="slot-tooltip-exo-menu"
                                buttonTooltip={TXT_ADD_EXO_HINT[lang] || TXT_ADD_EXO_HINT.en}
                                buttonContent={(
                                    <>
                                        <Add sx={{ fontSize: '1rem' }} />
                                        <span>{TXT_ADD_EXO[lang] || TXT_ADD_EXO.en}</span>
                                    </>
                                )}
                                highlightActiveItem={false}
                                items={exoChoices.map((stat) => ({
                                    label: localizedText(stat.label, lang),
                                    icon: <img src={stat.icon} alt="" />,
                                    // Starts at one point, so the line leaves the
                                    // rest of the 101 budget to the next exo; its
                                    // value is then edited in place like any other.
                                    onClick: () => onMageLine(stat.id, 1),
                                }))}
                            />
                        )}
                        {itemMaging && typeof onClearMaging === 'function' && (
                            <button
                                type="button"
                                className="slot-tooltip-maging-reset"
                                onClick={onClearMaging}
                            >
                                <RestartAlt sx={{ fontSize: '1rem' }} />
                                <span>{TXT_RESET_MAGING[lang] || TXT_RESET_MAGING.en}</span>
                            </button>
                        )}
                    </div>
                )}
                <StatsList
                    stats={statRows}
                    className="slot-tooltip-stats-col"
                    lang={lang}
                    onValueChange={canMage ? (stat, value) => onMageLine(stat.id, value) : null}
                />
                {showEffectRow && (
                    <>
                        <div className="slot-tooltip-divider" />
                        <div className="slot-tooltip-legendary-wrapper">
                            <Tooltip
                                content={
                                    effectDescription
                                        ? (
                                            <div className="slot-tooltip-legendary-tooltip">
                                                {effectDescription}
                                            </div>
                                        )
                                        : null
                                }
                            >
                                <div className="slot-tooltip-legendary-row">
                                    <img
                                        className="slot-tooltip-legendary-icon"
                                        src={ITEM_EFFECT_ICON}
                                        alt={effectName}
                                    />
                                    <span className="slot-tooltip-legendary-label">
                                        {effectName}
                                    </span>
                                </div>
                            </Tooltip>
                        </div>
                    </>
                )}
            </div>
            {hasWeaponMeta && (hasWeaponDamage || hasStats || showEffectRow) && (
                <div className="slot-tooltip-divider" />
            )}
            {hasWeaponMeta && (
                <WeaponMetaList
                    weapon={weapon}
                    lang={lang}
                    className="slot-tooltip-weapon-meta"
                />
            )}
            {info.conditions && (hasWeaponDamage || hasStats || showEffectRow || hasWeaponMeta) && (
                <div className="slot-tooltip-divider" />
            )}
            {info.conditions && (
                <div className="slot-tooltip-conditions">
                    {/* <div className='slot-tooltip-conditions-title'>{TXT_CONDITIONS[lang] || TXT_CONDITIONS.en}</div> */}
                    <div className='slot-tooltip-conditions-text'>{info.conditions}</div>
                </div>
            )}
        </div>
    );
});

export default SlotTooltip;
